/**
 * The tenders taxonomy — closed enums plus their Hebrew display labels.
 *
 * VENDORED COPY, THIRD HOP. The chain is:
 *   SOURCE OF TRUTH  tenders_etl/stages/04-normalize-enrich/src/taxonomy.ts
 *   BACKEND COPY     huaca/src/products/tenders/shared/taxonomy.ts
 *   HERE             huaca_app/apps/tenders/src/taxonomy.ts
 *
 * All three are BYTE-IDENTICAL below the marker line, so `diff` proves agreement and a sha256 can
 * guard it. src/taxonomy.drift.test.ts fails if this copy is edited by hand, and additionally
 * compares against the backend checkout when it is present as a sibling.
 *
 * WHY THE LABELS LIVE HERE RATHER THAN COMING FROM THE API. The filter sidebar has to render a
 * label for every value in a closed taxonomy — including the ones with a zero count, which the UI
 * shows disabled — and the posting page needs the Hebrew heading for each of the 15 `details`
 * fields. Serving those from the backend would mean a round trip before the filters can paint, for
 * data that changes only when the pipeline's own vocabulary changes.
 *
 * A few exports below are ETL-internal (`CITY_REGION`, `SOURCE_PUBLISHER`, `SOURCE_KIND`) and this
 * app never reads them. They are kept so the copy stays byte-identical and therefore checkable.
 */

/* ─── vendored body below ─── */
// Fixed facet enums. The LLM maps free Hebrew prose to these VALUES (constrained by the
// response schema); Hebrew display labels are attached deterministically here so labels never
// drift between runs. Filters use the value; the UI shows the label.

export const CATEGORIES = [
  'construction', 'infrastructure', 'it-software', 'engineering', 'consulting', 'transport',
  'logistics', 'cleaning', 'security', 'catering', 'healthcare', 'education', 'maintenance',
  'supplies', 'professional-services', 'finance', 'legal', 'marketing', 'hr', 'other',
] as const;
export type Category = (typeof CATEGORIES)[number];

export const REGIONS = [
  'north', 'haifa', 'center', 'tel-aviv', 'jerusalem', 'south', 'judea-samaria', 'national', 'other',
] as const;
export type Region = (typeof REGIONS)[number];

export const EMPLOYMENT_TYPES = [
  'full-time', 'part-time', 'temporary', 'contract', 'seasonal', 'volunteer', 'other',
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const TENDER_TYPES = [
  'open', 'framework', 'rfi', 'prequalification', 'auction', 'exemption', 'closed', 'other',
] as const;
export type TenderType = (typeof TENDER_TYPES)[number];

export const PROFESSIONS = [
  'engineer', 'civil-engineer', 'electrical-engineer', 'architect', 'lawyer', 'accountant',
  'economist', 'social-worker', 'teacher', 'nurse', 'doctor', 'it-specialist', 'project-manager',
  'administrator', 'driver', 'technician', 'laborer', 'cleaner', 'security-guard', 'inspector',
  'consultant', 'other',
] as const;
export type Profession = (typeof PROFESSIONS)[number];

export const PUBLISHER_TYPES = [
  'municipality', 'government', 'government-company', 'authority', 'healthcare-org', 'education-org', 'other',
] as const;
export type PublisherType = (typeof PUBLISHER_TYPES)[number];

export const CATEGORY_LABELS: Record<Category, string> = {
  construction: 'בינוי', infrastructure: 'תשתיות', 'it-software': 'מחשוב ותוכנה', engineering: 'הנדסה',
  consulting: 'ייעוץ', transport: 'תחבורה', logistics: 'לוגיסטיקה', cleaning: 'ניקיון', security: 'אבטחה',
  catering: 'הסעדה', healthcare: 'בריאות', education: 'חינוך', maintenance: 'אחזקה', supplies: 'אספקה',
  'professional-services': 'שירותים מקצועיים', finance: 'כספים', legal: 'משפטי', marketing: 'שיווק',
  hr: 'משאבי אנוש', other: 'אחר',
};
export const REGION_LABELS: Record<Region, string> = {
  north: 'צפון', haifa: 'חיפה', center: 'מרכז', 'tel-aviv': 'תל אביב', jerusalem: 'ירושלים',
  south: 'דרום', 'judea-samaria': 'יהודה ושומרון', national: 'ארצי', other: 'אחר',
};
export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  'full-time': 'משרה מלאה', 'part-time': 'משרה חלקית', temporary: 'זמני', contract: 'קבלן',
  seasonal: 'עונתי', volunteer: 'התנדבות', other: 'אחר',
};
export const TENDER_TYPE_LABELS: Record<TenderType, string> = {
  open: 'פומבי', framework: 'מסגרת', rfi: 'בקשה למידע', prequalification: 'מיון מוקדם',
  auction: 'התמחרות', exemption: 'פטור', closed: 'סגור', other: 'אחר',
};
export const PROFESSION_LABELS: Record<Profession, string> = {
  engineer: 'מהנדס', 'civil-engineer': 'מהנדס אזרחי', 'electrical-engineer': 'מהנדס חשמל',
  architect: 'אדריכל', lawyer: 'עורך דין', accountant: 'רואה חשבון', economist: 'כלכלן',
  'social-worker': 'עובד סוציאלי', teacher: 'מורה', nurse: 'אח/ות', doctor: 'רופא',
  'it-specialist': 'איש מחשוב', 'project-manager': 'מנהל פרויקט', administrator: 'מנהלה',
  driver: 'נהג', technician: 'טכנאי', laborer: 'עובד כללי', cleaner: 'עובד ניקיון',
  'security-guard': 'מאבטח', inspector: 'מפקח', consultant: 'יועץ', other: 'אחר',
};
export const PUBLISHER_TYPE_LABELS: Record<PublisherType, string> = {
  municipality: 'עירייה', government: 'ממשלה', 'government-company': 'חברה ממשלתית',
  authority: 'רשות', 'healthcare-org': 'ארגון בריאות', 'education-org': 'מוסד חינוך', other: 'אחר',
};

// City (he) → region. A deterministic pre-fill; the LLM confirms/fills when no city matches.
export const CITY_REGION: Record<string, Region> = {
  'תל אביב': 'tel-aviv', 'תל אביב-יפו': 'tel-aviv', 'רמת גן': 'tel-aviv', 'גבעתיים': 'tel-aviv',
  'חולון': 'tel-aviv', 'בת ים': 'tel-aviv', 'הרצליה': 'tel-aviv',
  'ירושלים': 'jerusalem', 'בית שמש': 'jerusalem',
  'חיפה': 'haifa', 'קריית אתא': 'haifa', 'נשר': 'haifa', 'טירת כרמל': 'haifa',
  'נתניה': 'center', 'כפר סבא': 'center', 'רעננה': 'center', 'הוד השרון': 'center', 'פתח תקווה': 'center',
  'ראש העין': 'center', 'לוד': 'center', 'רמלה': 'center', 'רחובות': 'center', 'ראשון לציון': 'center',
  'נס ציונה': 'center', 'יבנה': 'center', 'מודיעין': 'center', 'אור יהודה': 'center',
  'באר שבע': 'south', 'אשדוד': 'south', 'אשקלון': 'south', 'אילת': 'south', 'דימונה': 'south',
  'קריית גת': 'south', 'שדרות': 'south', 'נתיבות': 'south', 'אופקים': 'south',
  'נצרת': 'north', 'עפולה': 'north', 'טבריה': 'north', 'כרמיאל': 'north', 'קריית שמונה': 'north',
  'צפת': 'north', 'נהריה': 'north', 'עכו': 'north', 'בית שאן': 'north',
};

// Source (adapter key) → publisher identity. Fallback derives from the list-page hostname.
export const SOURCE_PUBLISHER: Record<string, { publisher: string; publisherType: PublisherType }> = {
  rail: { publisher: 'רכבת ישראל', publisherType: 'government-company' },
  'kfar-saba': { publisher: 'עיריית כפר סבא', publisherType: 'municipality' },
};

// Source (adapter key) → default kind, when text keywords are inconclusive.
export const SOURCE_KIND: Record<string, 'tender' | 'job'> = {
  rail: 'tender',
  'kfar-saba': 'job',
};

/**
 * The LLM `details` breakdown for public/government/municipal tenders + jobs: a field key and a
 * Hebrew description used in the prompt & response schema. Every field is a nullable Hebrew text
 * (the model fills only what the posting states). Keep this list in sync with `Details` in contract.ts.
 */
export const DETAIL_FIELDS = [
  { key: 'summary', desc: 'תקציר קצר (1-3 משפטים)' },
  { key: 'scope', desc: 'מהות והיקף ההתקשרות או המשרה' },
  { key: 'eligibility', desc: 'תנאי סף ודרישות חובה מהמציע/מועמד' },
  { key: 'experience', desc: 'ניסיון נדרש' },
  { key: 'technical', desc: 'דרישות טכניות ומפרט' },
  { key: 'submission', desc: 'אופן והוראות הגשת ההצעה/מועמדות' },
  { key: 'pricing', desc: 'הצעת מחיר, ערבויות, עלויות ותשלומים' },
  { key: 'clearance', desc: 'דרישות סיווג ובדיקות ביטחון' },
  { key: 'evaluation', desc: 'אמות מידה לבחירת הזוכה ושקלול' },
  { key: 'contractPeriod', desc: 'תקופת ההתקשרות ואופציות הארכה' },
  { key: 'schedule', desc: 'מועדים חשובים (מפגש, הבהרות, הגשה) כטקסט' },
  { key: 'updates', desc: 'עדכונים והקלות מהודעות העדכון: מה נדחה, מה שונה או הוקל, ומה תקף כעת' },
  { key: 'insurance', desc: 'דרישות ביטוח' },
  { key: 'subcontracting', desc: 'קבלני משנה או התאגדות מציעים' },
  { key: 'documentsRequired', desc: 'מסמכים שיש לצרף' },
  { key: 'contactNotes', desc: 'גורם מקשר, פרטי קשר ושאלות הבהרה' },
] as const;
export type DetailKey = (typeof DETAIL_FIELDS)[number]['key'];
